import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { InputField } from '../InputField';
import { SelectField } from '../SelectField';
import { TimePeriodSelector } from '../TimePeriodSelector';
import { convertToIndianWords } from '../../utils/numberToWords';
import { formatIndianCurrency } from '../../utils/formatCurrency';
import { formatIndianShort } from '../../utils/formatIndianShort';
import * as S from './CalculatorStyles';

export interface InputConfig {
  key: string;
  label: string;
  type?: 'number' | 'select';
  options?: { value: string | number; label: string }[];
  sliderConfig?: { min: number; max: number; step: number };
  showClear?: boolean;
  prefix?: string;
  hideWhen?: (values: Record<string, number>) => boolean;
}

export interface StatConfig {
  label: string;
  getValue: (values: Record<string, number>, result: number) => number;
  isWealth?: boolean;
  format?: (value: number) => string;
}

export interface ChartDataset {
  label: string;
  color: string;
}

export interface UniversalCalculatorConfig {
  title: string;
  subtitle: string;
  inputs: InputConfig[];
  useTimePeriod?: boolean;
  defaultValues: Record<string, number>;
  resultLabel: string;
  stats: StatConfig[];
  chartDatasets: ChartDataset[];
  calculate: (values: Record<string, number>) => number;
  generateChartData: (values: Record<string, number>, result: number) => number[][];
}

interface UniversalCalculatorProps {
  config: UniversalCalculatorConfig;
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' as const },
    tooltip: {
      enabled: window.innerWidth > 768,
      mode: 'index' as const,
      intersect: false,
      callbacks: {
        label: function (context: any) {
          const label = context.dataset.label || '';
          const value = context.parsed.y;
          return `${label}: ₹${formatIndianShort(value)}`;
        },
      },
    },
  },
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      ticks: {
        callback: function (value: any) {
          return formatIndianShort(value);
        },
      },
    },
  },
};

export const UniversalCalculator: React.FC<UniversalCalculatorProps> = ({ config }) => {
  const [values, setValues] = useState<Record<string, number>>(config.defaultValues);
  const [result, setResult] = useState(0);
  const [chartData, setChartData] = useState<any>(null);

  useEffect(() => {
    const calculatedResult = config.calculate(values);
    setResult(Math.round(calculatedResult));

    const dataArrays = config.generateChartData(values, calculatedResult);
    const labels = [];
    const currentYear = new Date().getFullYear();

    // Determine time period from values
    const totalYears = config.useTimePeriod ? (values.years || 0) + (values.months || 0) / 12 : 40;
    const maxYears = Math.ceil(totalYears);
    const step = maxYears <= 5 ? 1 : maxYears <= 10 ? 2 : 5;

    for (let year = 0; year <= maxYears; year += step) {
      labels.push(`${currentYear + year}`);
    }

    setChartData({
      labels,
      datasets: dataArrays.map((data, idx) => ({
        label: config.chartDatasets[idx].label,
        data,
        backgroundColor: config.chartDatasets[idx].color,
        borderColor: config.chartDatasets[idx].color,
        borderWidth: 1,
      })),
    });
  }, [values, config]);

  const updateValue = (key: string, value: number) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => setValues(config.defaultValues);

  return (
    <>
      <S.FormTitle>{config.title}</S.FormTitle>
      <S.Subtitle>{config.subtitle}</S.Subtitle>

      <S.ContentContainer>
        <S.InputGroup>
          {config.inputs.map((input) => {
            if (input.hideWhen && input.hideWhen(values)) return null;
            return input.type === 'select' ? (
              <SelectField
                key={input.key}
                label={input.label}
                value={values[input.key]}
                onChange={(val) => updateValue(input.key, typeof val === 'string' ? parseFloat(val) || 0 : val)}
                options={input.options || []}
              />
            ) : (
              <InputField
                key={input.key}
                label={input.label}
                value={values[input.key]}
                onChange={(val) => updateValue(input.key, val)}
                sliderConfig={input.sliderConfig}
                showClear={input.showClear}
                prefix={input.prefix}
              />
            );
          })}

          {config.useTimePeriod && (
            <TimePeriodSelector
              years={values.years || 0}
              months={values.months || 0}
              onYearsChange={(val) => updateValue('years', val)}
              onMonthsChange={(val) => updateValue('months', val)}
            />
          )}

          <S.ButtonContainer>
            <S.Button onClick={reset}>Reset</S.Button>
          </S.ButtonContainer>
        </S.InputGroup>

        <S.ResultsContainer>
          <S.AmountContainer>
            <S.FinalAmount>
              <S.AmountText>{config.resultLabel}</S.AmountText>
              <S.Amount>{formatIndianCurrency(result)}</S.Amount>
              <S.AmountInWords>{convertToIndianWords(result)} rupees only</S.AmountInWords>
            </S.FinalAmount>
            <hr />
            <S.StatsGrid columns={config.stats.length}>
              {config.stats.map((stat, idx) => (
                <S.StatBox key={idx}>
                  <S.StatLabel>{stat.label}</S.StatLabel>
                  <S.StatValue isWealth={stat.isWealth}>
                    {stat.format
                      ? stat.format(stat.getValue(values, result))
                      : formatIndianCurrency(stat.getValue(values, result))}
                  </S.StatValue>
                </S.StatBox>
              ))}
            </S.StatsGrid>
          </S.AmountContainer>

          <S.ChartContainer>
            {chartData && <Bar key={config.title} data={chartData} options={chartOptions} redraw />}
          </S.ChartContainer>
        </S.ResultsContainer>
      </S.ContentContainer>
    </>
  );
};
